import { createFileRoute, redirect } from '@tanstack/react-router';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider } from '@mui/material/styles';
import request from '@nineeng/request';
import { useCallback, useEffect, useState } from 'react';
import { SitesApiResponse, ContactsApiResponse, UserOverview } from '@repo/shared/domain';
import { EventEmitter, constructQuery } from '@repo/shared/helpers';
import CheckinPage from 'src/parts/app/CheckinPage';
import LanguagePage from 'src/parts/app/LanguagePage';
import RegistrationPage from 'src/parts/app/RegistrationPage';
import TasksPage from 'src/parts/app/TasksPage';
import { appTheme } from '@repo/theme';

const AUTO_TIMEOUT = 30 * 60; // 30 minutes

interface SearchParams {
	deviceId: string;
	deviceToken?: string;
	language?: string;
	role?: string;
	userId?: string;
}

export const Route = createFileRoute('/app/registration/')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		deviceId: search.deviceId as string,
		deviceToken: search.deviceToken as string | undefined,
		language: search.language as string | undefined,
		role: search.role as string | undefined,
		userId: search.userId as string | undefined,
	}),
	loaderDeps: ({ search }) => search,
	loader: async ({ deps }) => {
		const { deviceId, deviceToken, role, userId } = deps;
		const query = constructQuery({ deviceId, deviceToken });
		const noPaginationQuery = constructQuery({ deviceId, deviceToken, noPagination: 'true' });

		const { body: device } = await request.get(`/api/devices/${deviceId}?${query}`);

		if (device?.config?.kioskFixedFlow) {
			throw redirect({ to: '/app/registration/fixed', search: { deviceId, deviceToken } });
		}

		const [sitesRes, rolesRes, contactsRes, groupsRes] = await Promise.all([
			request.get(`/api/sites?${noPaginationQuery}`),
			request.get(`/api/roles/auto-complete?${noPaginationQuery}`),
			request.get(`/api/contacts?${noPaginationQuery}`),
			device?.config?.kioskRegistrationGroups
				? request.get(`/api/groups?${noPaginationQuery}`)
				: Promise.resolve({ body: { data: [], page: 0, pageSize: 0, count: 0, sort: 'asc' } }),
		]);

		let preloadedRegistrationData: UserOverview | undefined;
		if (userId) {
			try {
				const { body } = await request.get(
					`/api/devices/${deviceId}/user/${userId}/overview?${noPaginationQuery}`,
				);
				preloadedRegistrationData = body;
			} catch {
				preloadedRegistrationData = undefined;
			}
		}

		const allRoles = rolesRes.body;
		const applicableRoles = allRoles
			.filter((r: any) => device?.config?.kioskRoleIds?.includes(r.id))
			.filter((r: any) => (role ? r.id === role : true));

		const groups = (groupsRes.body?.data || []).map((group: any) => ({
			id: group.id,
			name: group.name,
		}));

		const defaultCountry = 'BE';

		return {
			auth: query,
			device,
			site: sitesRes.body?.data?.[0],
			roles: applicableRoles,
			contacts: contactsRes.body?.data || [],
			groups,
			preloadedRegistrationData,
			defaultCountry,
		};
	},
	component: RegistrationRoute,
});

function RegistrationRoute() {
	const { deviceId, language } = Route.useSearch();
	const {
		auth,
		device,
		site,
		roles,
		contacts,
		groups,
		preloadedRegistrationData,
		defaultCountry,
	} = Route.useLoaderData();

	const config = device?.config || {};
	const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

	const [isUploading, setUploading] = useState(false);
	const [registrationData, setRegistrationData] = useState<UserOverview | undefined>(preloadedRegistrationData);
	const [resultData, setResultData] = useState();
	const [eventId, setEventId] = useState();
	const [selectedPage, setSelectedPage] = useState(
		language ? (preloadedRegistrationData ? 'tasks' : 'registration') : 'language',
	);
	const [lng, setLng] = useState(language ? language : 'EN');

	const handleLanguage = (lng: string) => {
		setLng(lng);
		setSelectedPage(preloadedRegistrationData ? 'tasks' : 'registration');
	};

	const handleClose = () => {
		window.parent.postMessage({ close: true, registration: true }, '*');
	};

	const handleUserReload = useCallback(async () => {
		if (!registrationData?.user?.id) return;
		const { body } = await request.get(`/api/devices/${deviceId}/user/${registrationData.user.id}/overview?${auth}`);
		setRegistrationData(body);
		setSelectedPage('tasks');
	}, [auth, deviceId, registrationData?.user?.id]);

	const handleRegistration = async (data: any) => {
		if (isUploading) return;
		setUploading(true);

		try {
			const { body } = await request.post(`/api/devices/${deviceId}/registration?${auth}`, data);
			setUploading(false);
			setRegistrationData(body);
			setEventId(body?.event?.id);
			setSelectedPage('tasks');
		} catch (e: any) {
			console.error(e);
			EventEmitter.emit('toast', { type: 'error', message: e.message });
			setUploading(false);
			throw e;
		}
	};

	const handleCheckin = async () => {
		if (!registrationData) return;

		const { body } = await request.get(
			`/api/devices/${deviceId}/checkin/${registrationData.user.id}?${auth}&action=checkin&eventId=${eventId}`,
		);

		window.parent.postMessage({ checkedIn: true, hasAccess: body.hasAccess }, '*');
		setResultData(body);
		setSelectedPage('checkin');
	};

	const handlePrintBadge = () => {
		if (!baseUrl) return;
		const userId = registrationData?.user.id;
		const printUrl = `${baseUrl}/api/devices/${deviceId}/user/badge?userId=${userId}&eventId=`;
		window.parent.postMessage({ printUrl }, '*');
		setTimeout(handleClose, 500);
	};

	useEffect(() => {
		window.parent.postMessage({ isReady: true }, '*');
		setTimeout(handleClose, AUTO_TIMEOUT * 1000);
		handleUserReload();
	}, [handleUserReload]);

	const pages: Record<string, React.ReactNode> = {
		language: <LanguagePage onLanguage={handleLanguage} />,
		registration: (
			<RegistrationPage
				config={config}
				site={site}
				roles={roles}
				contacts={contacts}
				groups={groups}
				onSubmit={handleRegistration}
				lng={lng}
				country={defaultCountry}
			/>
		),
		tasks: (
			<TasksPage
				auth={auth}
				user={registrationData?.user}
				tasks={registrationData?.openTasks}
				onReload={handleUserReload}
				onComplete={handleCheckin}
				lng={lng}
			/>
		),
		checkin: (
			<CheckinPage
				result={resultData}
				onClose={handleClose}
				onPrint={handlePrintBadge}
				lng={lng}
				printerConfigured={!!config.printerUrl}
			/>
		),
	};

	return (
		<ThemeProvider theme={appTheme}>
			<div className="absolute top-0 bottom-0 left-0 right-0">
				{pages[selectedPage]}

				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUploading}>
					<CircularProgress color="inherit" />
				</Backdrop>

				<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!roles.length}>
					<Alert severity="warning">Invalid configuration, no roles available for self-registration.</Alert>
				</Backdrop>
			</div>
		</ThemeProvider>
	);
}
